// NOTE: This file is lint-protected. Use double quotes for all strings.
// Avoid nested quotes; rephrase if needed to keep syntax safe.

export const journeyFlows = {
  buy: [
    // ============================================================
    // PHASE 1: CLARIFY THE CAR DECISION
    // ============================================================
    {
      id: "car-buying-game-plan",
      title: "Your car-buying journey",
      cards: [],
      dataKey: "gamePlanSeen"
    },
    {
      id: "vehicle-type",
      title: "New or Used?",
      cards: [],
      dataKey: "vehicleType"
    },
    {
      id: "new-car-trade-off-map",
      title: "New car vs used car trade-offs",
      cards: [],
      dataKey: "tradeOffMapSeen",
      condition: (data) => data.vehicleType === "New Vehicle"
    },
    {
      id: "shortlist-builder",
      title: "Define your needs and build a shortlist",
      cards: [],
      dataKey: "shortlistBuilderSeen"
    },
    // ============================================================
    // PHASE 2: SET A SAFE BUDGET
    // ============================================================
    {
      id: "budget-check-step",
      title: "Budget check",
      cards: [],
      dataKey: "budgetCheckSeen"
    },
    // ============================================================
    // PHASE 3: FINANCING PREP
    // ============================================================
    {
      id: "payment-method",
      title: "How Will You Pay?",
      cards: [
        {
          title: "Cash",
          pros: [
            "No interest charges",
            "Own the car outright immediately",
            "Stronger negotiating position"
          ],
          cons: [
            "Ties up large sum of money",
            "Dealers may be less flexible (they profit from financing)",
            "Lost opportunity for investment returns"
          ],
          watchOut: "**Never** reveal you're paying cash until after you agree on the out-the-door price. Negotiate the price first, then mention cash."
        },
        {
          title: "Financing",
          pros: [
            "Preserve cash for emergencies",
            "Build credit with on-time payments",
            "Potentially invest the cash elsewhere"
          ],
          cons: [
            "Interest charges add to total cost",
            "Risk of APR markup by dealer",
            "Longer commitment"
          ],
          watchOut: "Get pre-approval from your credit union or bank BEFORE shopping. This protects you from dealer rate markups."
        }
      ],
      insight: "**Pro Tip:** Even if you're paying cash, get a pre-approval. It gives you leverage to negotiate a better price, then you can pay cash after agreeing on the OTD total.",
      dataKey: "paymentMethod"
    },
    {
      id: "financing-source",
      title: "Where Will You Get Financing?",
      condition: (data) => data.paymentMethod === "financing",
      cards: [
        {
          title: "Credit Union",
          pros: [
            "Typically 0.5-2% lower APR",
            "Member-owned, not profit-driven",
            "Often waive or reduce fees"
          ],
          cons: [
            "Must join (usually $5-25 fee)",
            "May have membership requirements",
            "Slightly more paperwork"
          ],
          watchOut: "Many credit unions are open to anyone for a small membership fee. Shop rates before assuming you can't join."
        },
        {
          title: "Bank",
          pros: [
            "Existing relationship may help",
            "Familiar process",
            "Often have auto loan promotions"
          ],
          cons: [
            "Rates typically higher than credit unions",
            "May have origination fees",
            "Less flexible than credit unions"
          ],
          watchOut: "If you have excellent credit, banks may match credit union rates. Always compare."
        },
        {
          title: "Dealer Financing",
          pros: [
            "Convenient-one-stop shopping",
            "May offer manufacturer incentives",
            "Can potentially negotiate rate"
          ],
          cons: [
            "Risk of APR markup (sell rate vs buy rate)",
            "Pressure to buy add-ons",
            "May try to focus on monthly payment only"
          ],
          watchOut: "Always ask: What is my buy rate from the lender? The dealer should print the bank's approval showing the actual rate you qualified for."
        }
      ],
      insight: "**Pro Tip:** Get pre-approval from 2-3 sources (credit union, bank, online lender) within a 2-week window. Multiple inquiries during this period typically count as just one for your credit score.",
      dataKey: "financingSource",
      savingsKey: "apr-padding",
      savingsCalc: (choice) => {
        if (choice === "credit-union" || choice === "bank") {
          return { amount: 1400, description: "Avoiding 2% APR markup on $25K loan over 60 months" };
        }
        return null;
      }
    },
    {
      id: "insurance",
      title: "Insurance Quotes",
      cards: [
        {
          title: "Get Quotes Before Buying",
          pros: [
            "Know total monthly cost (payment + insurance)",
            "Avoid surprises after purchase",
            "Can influence vehicle choice"
          ],
          cons: [
            "Takes extra time",
            "Need VIN or trim details",
            "Quotes may vary slightly from final"
          ],
          watchOut: "Some trims can raise insurance $40-80/month. A small upgrade can cost you $2,000+ over 3 years in extra premiums."
        }
      ],
      insight: "**Pro Tip:** Get quotes for the exact VIN or trim level. Performance packages, turbo engines, or 'sport' badges can spike insurance costs significantly.",
      dataKey: "insurance"
    },
    // ============================================================
    // PHASE 4: TRADE-IN PREPARATION
    // ============================================================
    {
      id: "trade-in-check",
      title: "Do you have a car to sell or trade in?",
      cards: [],
      dataKey: "hasTradeInInitial"
    },
    {
      id: "trade-in-benchmark",
      title: "Let's benchmark your current car",
      cards: [],
      dataKey: "tradeInBenchmarkComplete",
      condition: (data) => data.hasTradeInInitial === true
    },
    {
      id: "buy-trade-offers-organizer",
      title: "Enter your online offers",
      cards: [],
      dataKey: "tradeOffersEntered",
      condition: (data) => data.hasTradeInInitial === true && data.tradeInBenchmarkComplete === true
    },
    {
      id: "offer-usage-choice",
      title: "How do you want to use your offers?",
      cards: [],
      dataKey: "offerUsagePath",
      condition: (data) => data.hasTradeInInitial === true && data.tradeInBenchmarkComplete === true
    },
    {
      id: "trade-sell-online-tips",
      title: "Sell your car to an online buyer",
      cards: [],
      dataKey: "sellOnlineTipsSeen",
      condition: (data) => data.offerUsagePath === "sell-online"
    },
    {
      id: "trade-dealer-leverage-tips",
      title: "Use your offers as leverage at the dealership",
      cards: [],
      dataKey: "dealerLeverageTipsSeen",
      condition: (data) => data.offerUsagePath === "dealer-leverage"
    },
    // ============================================================
    // PHASE 5: DEALER PSYCHOLOGY + URGENCY EDUCATION
    // (Consolidated: loss aversion, urgency tactics, scripts, pressure framing)
    // ============================================================
    {
      id: "leverage-intro",
      title: "Keep emotion out of the decision",
      cards: [],
      dataKey: "leverageIntroSeen"
    },
    // ============================================================
    // PHASE 6: TEST DRIVE GUIDANCE
    // ============================================================
    {
      id: "test-drive-step",
      title: "If needed, test drive (but don't buy)",
      cards: [],
      dataKey: "testDriveStepSeen"
    },
    // ============================================================
    // PHASE 7: COLLECT WRITTEN OUT-THE-DOOR QUOTES
    // ============================================================
    {
      id: "otd-strategy",
      title: "Getting Out-the-Door Quotes",
      cards: [
        {
          title: "The OTD Request",
          pros: [
            "Forces transparency upfront",
            "Easy to compare across dealers",
            "Eliminates surprise fees"
          ],
          cons: [
            "Some dealers resist providing it",
            "Takes time to contact multiple dealers",
            "May get follow-up calls/emails"
          ],
          watchOut: "If a dealer won't provide a written OTD quote, that's your red flag. Walk away."
        }
      ],
      insight: "**Pro Tip:** Email 3-5 dealers with: Please send itemized OTD price for [Year/Make/Model/VIN]. Include vehicle price, taxes, title/registration, doc fee, and any add-ons separately. I use OTD total to decide where to buy.",
      dataKey: "otdStrategy"
    },
    // ============================================================
    // PHASE 8: NEGOTIATE USING BEST QUOTE
    // ============================================================
    {
      id: "negotiation",
      title: "Negotiation Day",
      cards: [
        {
          title: "The Day-Of Checklist",
          pros: [
            "Organized approach",
            "Less likely to miss red flags",
            "Confidence in the process"
          ],
          cons: [
            "Dealer may rush you",
            "Takes time to verify everything",
            "May need to walk away"
          ],
          watchOut: "The last 2 minutes before signing are where most mistakes happen. Slow down and verify every detail."
        }
      ],
      insight: "**Pro Tip:** Before signing, verify: OTD matches quote, VIN and mileage are correct, APR and term are as agreed, no blanks on forms, and you have copies of everything.",
      dataKey: "negotiation"
    },
    {
      id: "add-ons",
      title: "Understanding Add-Ons",
      cards: [
        {
          title: "GAP Insurance",
          pros: [
            "Covers loan balance if car is totaled",
            "Peace of mind if you put little down",
            "Can be canceled later for refund"
          ],
          cons: [
            "Dealer price often 2-3× insurer's price",
            "May not need it if you put 20%+ down",
            "Adds interest if financed"
          ],
          watchOut: "Get a quote from your auto insurance company first. Dealer GAP is often $800-1,000 vs $200-400 from your insurer."
        },
        {
          title: "Extended Warranty (VSC)",
          pros: [
            "Covers repairs after factory warranty",
            "Can provide peace of mind",
            "Transferable if you sell the car"
          ],
          cons: [
            "Expensive ($1,500-3,000+)",
            "Many exclusions and deductibles",
            "Reliable cars rarely need them"
          ],
          watchOut: "Read the exclusions first. Most warranties don't cover wear items, and claims require proof of maintenance."
        },
        {
          title: "VIN Etching / Paint Sealant / Nitrogen",
          pros: [
            "Some have marginal value",
            "May deter theft (VIN etching)"
          ],
          cons: [
            "Grossly overpriced at dealer",
            "Can be done yourself for 10% of cost",
            "Often bundled without consent"
          ],
          watchOut: "These are almost always optional. Ask for each item to be priced separately, then decline or buy elsewhere for less."
        }
      ],
      insight: "**Pro Tip:** Every dollar in add-ons is money you pay interest on if it's rolled into the loan. $2,000 in add-ons financed at 7% over 60 months costs you about $2,369 total.",
      dataKey: "addOns",
      savingsKey: "add-on-awareness",
      savingsCalc: () => {
        return { amount: 1800, description: "Avoiding unnecessary dealer add-on packages" };
      }
    },
    {
      id: "inspection",
      title: "Pre-Purchase Inspection",
      condition: (data) => data.vehicleType === "Used Vehicle" || data.vehicleType === "used" || data.vehicleType === "cpo",
      cards: [
        {
          title: "Independent Mechanic Inspection",
          pros: [
            "Uncovers hidden issues",
            "Leverage for price negotiation",
            "Peace of mind"
          ],
          cons: [
            "Costs $100-250",
            "Takes time",
            "May find deal-breakers"
          ],
          watchOut: "If the dealer refuses to allow an inspection, that's a massive red flag. Walk away immediately."
        }
      ],
      insight: "**Pro Tip:** A $150 inspection can reveal $2,000+ in needed repairs. Always insist on it, and if the dealer won't allow it, that's your sign to leave.",
      dataKey: "inspection"
    },
    // ============================================================
    // PHASE 9: CHECK YOUR DEAL (Final validation step)
    // ============================================================
    {
      id: "signing",
      title: "Check Your Deal",
      cards: [
        {
          title: "Contract Review",
          pros: [
            "Legal protection",
            "Clarity on all terms",
            "Written record"
          ],
          cons: [
            "Lots of paperwork",
            "Dealer may rush",
            "Some forms are digital"
          ],
          watchOut: "Never sign blank forms or on a tablet without seeing printed copies first. Hidden fees are easiest to slip past on digital signatures."
        }
      ],
      insight: "**Pro Tip:** Take photos of every signed page and both sides of any checks. If a dispute arises, you have proof of what you signed.",
      dataKey: "signing"
    },
    // ============================================================
    // PHASE 10: SIGN CONFIDENTLY
    // ============================================================
    {
      id: "completion",
      title: "Sign Confidently",
      cards: [
        {
          title: "You've Completed the Journey",
          pros: [
            "Armed with knowledge",
            "Avoided common traps",
            "Positioned for a better deal"
          ],
          cons: [],
          watchOut: ""
        }
      ],
      insight: "**Remember:** Keep all documents, verify your title arrives within 30-60 days, and review your first loan statement to ensure terms match what you signed.",
      dataKey: "completion",
      isComplete: true
    }
  ],
  
  lease: [
    {
      id: "lease-vs-buy",
      title: "Is Leasing Right for You?",
      cards: [
        {
          title: "Lease Makes Sense If:",
          pros: [
            "You want a new car every 3-4 years",
            "You drive under 12,000 miles/year",
            "You value warranty coverage",
            "You prefer lower monthly payments"
          ],
          cons: [
            "You're perpetually making payments",
            "Strict mileage limits ($0.15-0.30/mile over)",
            "No equity or ownership",
            "Early termination is expensive"
          ],
          watchOut: "If you drive over 15,000 miles/year, leasing penalties can cost thousands. Buying is usually better for high-mileage drivers."
        },
        {
          title: "Buy Instead If:",
          pros: [
            "You plan to keep the car 8+ years",
            "You drive more than 15,000 miles/year",
            "You want to customize the vehicle",
            "You want to build equity"
          ],
          cons: [
            "Higher monthly payment",
            "You absorb all depreciation",
            "Responsible for all repairs after warranty"
          ],
          watchOut: "Buying maximizes value if you keep the vehicle long enough to have years of payment-free ownership."
        }
      ],
      insight: "**Pro Tip:** The 8-Year Rule: Buy if you'll keep it 8+ years. Lease if you want a new car every 3-4 years. In-between? Consider 2-3 year old CPO (certified pre-owned).",
      dataKey: "leaseVsBuy"
    },
    {
      id: "lease-terms",
      title: "Understanding Lease Terms",
      cards: [
        {
          title: "Money Factor",
          pros: [
            "Lower number = better deal",
            "Transparent if dealer shows buy rate",
            "Can be negotiated (if marked up)"
          ],
          cons: [
            "Written as confusing decimal",
            "Dealers may mark it up",
            "Most people don't know to check it"
          ],
          watchOut: "To convert Money Factor to APR: Multiply by 2,400. Example: 0.0025 × 2,400 = 6% APR."
        },
        {
          title: "Residual Value",
          pros: [
            "Higher residual = lower payment",
            "Set by bank, not negotiable",
            "Predictable end-of-lease value"
          ],
          cons: [
            "If actual value is lower, you can't walk away from the buyout",
            "Some brands have better residuals than others"
          ],
          watchOut: "The residual is the bank's guess at what the car will be worth. Higher residuals mean the car holds value better."
        },
        {
          title: "Capitalized Cost (Cap Cost)",
          pros: [
            "This is the selling price-negotiate it!",
            "Lower cap cost = lower payment",
            "Same negotiation tactics as buying"
          ],
          cons: [
            "Dealers may hide add-ons in cap cost",
            "Harder to compare across dealers",
            "May include fees"
          ],
          watchOut: "Ask for the cap cost to be itemized: vehicle price + fees + add-ons. Negotiate the vehicle price down, just like a purchase."
        }
      ],
      insight: "**Pro Tip:** Always ask: 'What is the base money factor and residual percentage from the bank?' If the dealer won't show you, they may be marking it up.",
      dataKey: "leaseTerms",
      savingsKey: "lease-mf-markup",
      savingsCalc: () => {
        return { amount: 800, description: "Catching and negotiating money factor markup" };
      }
    },
    {
      id: "lease-fees",
      title: "Lease Fees",
      cards: [
        {
          title: "Acquisition Fee",
          pros: [
            "Standard bank fee",
            "Covers processing costs"
          ],
          cons: [
            "Usually $500-900",
            "Rarely negotiable",
            "Added to cap cost"
          ],
          watchOut: "Acquisition fees are set by the bank, not the dealer. They're typically non-negotiable but should be disclosed upfront."
        },
        {
          title: "Disposition Fee",
          pros: [
            "Only charged if you return the car",
            "Waived if you buy the car or lease another from same brand"
          ],
          cons: [
            "Usually $300-600",
            "Easy to forget about",
            "Charged at lease end"
          ],
          watchOut: "Factor this into your total lease cost. If you return the car, you'll pay this fee at the end."
        }
      ],
      insight: "**Pro Tip:** Add up all fees (acquisition + disposition + any dealer fees) to see the true cost. These can add $1,000-1,500 to your lease.",
      dataKey: "leaseFees"
    },
    {
      id: "down-payment",
      title: "Down Payment Risk",
      cards: [
        {
          title: "Why Big Down Payments Are Risky",
          pros: [
            "Lowers monthly payment",
            "May reduce interest charges slightly"
          ],
          cons: [
            "If car is totaled, you lose the down payment",
            "Insurance pays the bank, not you",
            "Doesn't reduce total lease cost much"
          ],
          watchOut: "Never put more than $1,000-2,000 down on a lease. If the car is totaled or stolen in month 2, your down payment is gone."
        }
      ],
      insight: "**Pro Tip:** Compare the total cost with zero down vs. a big down payment. Often the zero-down option is actually cheaper overall and protects your cash.",
      dataKey: "downPayment",
      savingsKey: "lease-down-risk",
      savingsCalc: () => {
        return { amount: 3000, description: "Avoiding lost down payment if vehicle is totaled early" };
      }
    },
    {
      id: "mileage",
      title: "Mileage Limits",
      cards: [
        {
          title: "Standard Mileage",
          pros: [
            "Typical 10,000-12,000/year",
            "Lower base payment"
          ],
          cons: [
            "$0.15-0.30 per mile if you go over",
            "Can't \"bank\" unused miles",
            "Penalties add up fast"
          ],
          watchOut: "If you drive 20,000 miles/year with a 12K lease, you'll have 24,000 excess miles over 3 years. At $0.25/mile, that's a $6,000 penalty!"
        },
        {
          title: "High-Mileage Lease",
          pros: [
            "Buy extra miles upfront (cheaper)",
            "Usually $0.10-0.15/mile vs $0.25+ at end",
            "No surprise bill at lease end"
          ],
          cons: [
            "Higher monthly payment",
            "Can't get refund for unused miles",
            "Still has a limit"
          ],
          watchOut: "If you know you drive a lot, buying extra miles upfront is usually cheaper than paying penalties later."
        }
      ],
      insight: "**Pro Tip:** Track your annual mileage for 3-6 months before leasing. Multiply by 12 to estimate yearly, then add a buffer. It's better to overestimate than underestimate.",
      dataKey: "mileage"
    },
    {
      id: "gap-insurance-lease",
      title: "GAP Insurance for Leases",
      cards: [
        {
          title: "GAP Coverage",
          pros: [
            "Often included in lease (check your contract)",
            "Protects you if car is totaled",
            "Required by most lessors"
          ],
          cons: [
            "May be charged separately by dealer",
            "Can be expensive if not included",
            "May have coverage limits"
          ],
          watchOut: "Many leases include GAP automatically. Ask: \"Is GAP included in my lease, or is it an additional charge?\""
        }
      ],
      insight: "**Pro Tip:** If GAP isn't included, get a quote from your auto insurer before accepting the dealer's price.",
      dataKey: "gapLease"
    },
    {
      id: "end-of-lease",
      title: "End-of-Lease Options",
      cards: [
        {
          title: "Return the Car",
          pros: [
            "Walk away (after inspection)",
            "No hassle of selling",
            "Disposition fee only ($300-600)"
          ],
          cons: [
            "Must pay for excess wear and tear",
            "Mileage overage charges",
            "No equity to show for payments"
          ],
          watchOut: "Get the car detailed before the inspection. Small touch-ups can save you hundreds in wear-and-tear charges."
        },
        {
          title: "Buy the Car",
          pros: [
            "You know the car's history",
            "No disposition fee",
            "Can finance the buyout"
          ],
          cons: [
            "Buyout price may be higher than market value",
            "You're buying a used car at predetermined price",
            "Sales tax on buyout (in most states)"
          ],
          watchOut: "Compare the residual (buyout price) to the car's actual market value before deciding. Sometimes you're better off returning it."
        },
        {
          title: "Lease Another",
          pros: [
            "Stay in a new car with warranty",
            "Disposition fee often waived",
            "Familiar process"
          ],
          cons: [
            "Perpetual payments",
            "Never build equity",
            "May be upsold on new lease"
          ],
          watchOut: "Dealers may pressure you to lease again. Take time to compare buying vs leasing based on your current situation."
        }
      ],
      insight: "**Pro Tip:** About 3 months before lease end, check the car's market value vs. your residual. If market value is higher, you have equity (rare but possible).",
      dataKey: "endOfLease"
    },
    {
      id: "lease-completion",
      title: "Lease Journey Complete!",
      cards: [
        {
          title: "You're Ready to Lease Smart",
          pros: [
            "Understand money factor and residual",
            "Know about fee structures",
            "Aware of common pitfalls"
          ],
          cons: [],
          watchOut: ""
        }
      ],
      insight: "**Remember:** Always get the money factor, residual, cap cost, and all fees in writing before signing. Compare multiple dealers' lease offers using total cost, not just monthly payment.",
      dataKey: "leaseCompletion",
      isComplete: true
    }
  ],
  
  trade: [
    {
      id: "trade-value",
      title: "Know Your Car's Value",
      cards: [
        {
          title: "Get Multiple Offers",
          pros: [
            "Typical spread is $1,000-$2,000",
            "30 minutes of work = $1,500 gain",
            "Leverage against dealer lowballs"
          ],
          cons: [
            "Takes time",
            "Need to provide VIN and details",
            "Offers may vary by condition"
          ],
          watchOut: "The first offer is rarely the best. Get at least 3-5 written offers to establish your baseline."
        }
      ],
      insight: "**Pro Tip:** Get offers from: KBB Instant Cash Offer, CarMax, Carvana, Edmunds, and 2-3 local dealers. Use the highest as your floor.",
      dataKey: "tradeValue",
      savingsKey: "trade-offers",
      savingsCalc: () => {
        return { amount: 1500, description: "Getting multiple trade offers vs accepting first" };
      }
    },
    {
      id: "payoff-amount",
      title: "Get Your Payoff Amount",
      cards: [
        {
          title: "10-Day Payoff",
          pros: [
            "Tells you exactly what you owe",
            "Essential for equity calculation",
            "Available from lender app/phone"
          ],
          cons: [
            "Changes daily (accrues interest)",
            "Must be recent (within 10 days)",
            "Payoff ≠ remaining balance"
          ],
          watchOut: "Payoff amount is usually higher than your remaining balance because it includes accrued interest through the payoff date."
        }
      ],
      insight: "**Pro Tip:** Call your lender or check your account app for \"10-day payoff\" or \"payoff quote.\" This is the exact amount needed to clear the loan.",
      dataKey: "payoff"
    },
    {
      id: "equity-status",
      title: "Calculate Your Equity",
      cards: [
        {
          title: "Positive Equity",
          pros: [
            "Car is worth more than you owe",
            "You can use it as down payment",
            "Strengthens negotiating position"
          ],
          cons: [
            "May miss out if you sell privately for even more"
          ],
          watchOut: "Positive equity can be applied to your new purchase, reducing the amount you need to finance."
        },
        {
          title: "Negative Equity",
          pros: [
            "Understanding it helps you plan",
            "Can be addressed with cash or waiting"
          ],
          cons: [
            "You owe more than car is worth",
            "Rolling it into new loan is risky",
            "Starts new loan underwater"
          ],
          watchOut: "If you're more than $2,000 underwater, seriously consider paying down the difference or selling privately before buying."
        }
      ],
      insight: "**Pro Tip:** Equity = Trade Value - Payoff. If positive, great. If negative over $1,500, pause and consider your options before proceeding.",
      dataKey: "equityStatus"
    },
    {
      id: "private-vs-dealer",
      title: "Private Sale vs Trade-In",
      cards: [
        {
          title: "Trade-In at Dealer",
          pros: [
            "Convenient one-stop transaction",
            "Potential sales tax credit",
            "No need to advertise or meet buyers"
          ],
          cons: [
            "Usually $1,000-$2,000 less than private",
            "Dealer may lowball",
            "Can complicate negotiation"
          ],
          watchOut: "Tax credit varies by state. Some states let you pay sales tax on the difference (new price - trade value), which can save $500-1,000."
        },
        {
          title: "Private Sale",
          pros: [
            "Typically nets $1,000-$2,000 more",
            "You control the process",
            "Can wait for right buyer"
          ],
          cons: [
            "Takes time and effort",
            "Need to advertise and show car",
            "No tax credit benefit",
            "Some safety concerns"
          ],
          watchOut: "Follow the 7-step private sale safety protocol: meet at buyer's bank, verify funds, notarize, file release of liability same day."
        }
      ],
      insight: "**Pro Tip:** Calculate the net after tax credit. Sometimes the trade-in is close enough to private sale that the convenience is worth it.",
      dataKey: "tradeVsPrivate"
    },
    {
      id: "trade-break-even",
      title: "Trade-In Break Even Calculator",
      type: "trade-break-even-calculator",
      description: "Compare your outside offer vs the dealer trade, including tax credit if your state allows it.",
      dataKey: "tradeBreakEvenSeen"
    },
    {
      id: "negative-equity-options",
      title: "Dealing with Negative Equity",
      condition: (data) => data.equityStatus === "negative",
      cards: [
        {
          title: "Pay Down the Difference",
          pros: [
            "Start fresh with new car",
            "Avoid rolling debt forward",
            "Better loan terms"
          ],
          cons: [
            "Requires cash on hand",
            "May not be feasible for everyone"
          ],
          watchOut: "If you can afford it, this is usually the best option. You avoid compounding the problem."
        },
        {
          title: "Roll Into New Loan",
          pros: [
            "Don't need cash upfront",
            "Can proceed with purchase"
          ],
          cons: [
            "Start new loan underwater",
            "Pay interest on old debt",
            "May exceed loan-to-value limits"
          ],
          watchOut: "Only roll if it's under $1,500 AND you're keeping the new car for 8+ years. Otherwise, you're compounding the problem."
        },
        {
          title: "Wait and Pay Down",
          pros: [
            "Reduces or eliminates negative equity",
            "Better financial position later",
            "More options when ready"
          ],
          cons: [
            "Delays new car purchase",
            "May take 6-18 months"
          ],
          watchOut: "If you're $5,000+ underwater, this is often the smartest move. It's hard to wait, but it prevents a bad situation from getting worse."
        }
      ],
      insight: "**Pro Tip:** Rolling negative equity is like paying off one loan by taking out another-the balance never goes away, it just shifts and compounds.",
      dataKey: "negativeEquityOption"
    },
    {
      id: "trade-timing",
      title: "When to Mention Your Trade",
      cards: [
        {
          title: "Negotiate Separately",
          pros: [
            "Keeps the deals clear",
            "Harder for dealer to manipulate",
            "Better transparency"
          ],
          cons: [
            "Takes more time",
            "Some dealers resist",
            "Need to stay organized"
          ],
          watchOut: "Don't reveal your trade until after you've agreed on the out-the-door price of the new car. This prevents dealers from adjusting numbers to hide profit."
        }
      ],
      insight: "**Pro Tip:** The dealer will ask \"Are you trading anything in?\" early. Say: \"Yes, but let's agree on your car's price first, then we'll discuss my trade separately.\"",
      dataKey: "tradeTiming",
      savingsKey: "trade-negotiation",
      savingsCalc: () => {
        return { amount: 800, description: "Separating trade negotiation from new car price" };
      }
    },
    {
      id: "trade-completion",
      title: "Trade-In Journey Complete!",
      cards: [
        {
          title: "You're Ready to Trade Smart",
          pros: [
            "Know your car's value",
            "Understand equity position",
            "Have multiple offers as leverage"
          ],
          cons: [],
          watchOut: ""
        }
      ],
      insight: "**Remember:** Multiple offers are your leverage. If a dealer won't match your best written offer, walk away. The typical spread is $1,000-$2,000, so it's worth shopping around.",
      dataKey: "tradeCompletion",
      isComplete: true
    }
  ],
  
  learn: [
    {
      id: "learn-welcome",
      title: "Welcome to the Learning Center",
      cards: [
        {
          title: "Explore at Your Own Pace",
          pros: [
            "No pressure to buy",
            "Build knowledge first",
            "Make informed decisions later"
          ],
          cons: [],
          watchOut: ""
        }
      ],
      insight: "Browse our comprehensive guides on Getting Started, Dealer Tactics, Financing, Leasing, and Trade-Ins. Each lesson is designed to give you the knowledge dealers don't want you to have.",
      dataKey: "learnWelcome"
    }
  ]
};

export const getFlowSteps = (pathKey) => {
  return journeyFlows[pathKey] || [];
};

export const getStep = (pathKey, stepId) => {
  const flow = journeyFlows[pathKey];
  if (!flow) return null;
  return flow.find(step => step.id === stepId);
};

export default journeyFlows;